// Module frontend surface.
//
// The shell discovers this file automatically (modules/*/frontend/index.js) and
// merges what it exports. Adding this module needs no edit to the shell.
//
// Fourteen routes for four jobs, and the split is by who is doing them:
// dirección reads the panel and the briefing, the analyst maintains the
// indicators and the reports, whoever buys looks at the suppliers, and only
// dirección decides. Each has its own permission, so the sidebar a buyer sees
// and the one a commercial manager sees are different sidebars.

// With the extension, and it matters: Vite resolves './i18n/es' happily, but
// Node's ESM loader does not — and the shell's i18n guard imports this file with
// plain Node. Without the four characters the module's translations are
// invisible to the test that exists to check them.
import es from './i18n/es.js'
import ca from './i18n/ca.js'
import en from './i18n/en.js'
import fr from './i18n/fr.js'

export const routes = [
  // ── The panel ──────────────────────────────────────────────────────────────
  {
    path: '/markets',
    name: 'markets',
    component: () => import('./pages/MarketsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.home.title' },
  },
  {
    path: '/markets/radar',
    name: 'markets-radar',
    component: () => import('./pages/RadarPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.radar.title' },
  },

  // ── The catalogue of what is watched ───────────────────────────────────────
  // Two routes onto ONE page, and that is the design rather than a shortcut: a
  // raw material is an indicator whose kind is commodity or energy (see the
  // backend's data/models.js). Two screens, one collection, one set of
  // arithmetic — so «+8 %» cannot mean two different things.
  {
    path: '/markets/commodities',
    name: 'markets-commodities',
    component: () => import('./pages/IndicatorsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.commodities.title', commodities: true },
  },
  {
    path: '/markets/indicators',
    name: 'markets-indicators',
    component: () => import('./pages/IndicatorsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.indicators.title' },
  },
  {
    path: '/markets/indicators/:id',
    name: 'markets-indicator',
    component: () => import('./pages/IndicatorPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.indicators.title' },
  },

  // ── What it costs us ───────────────────────────────────────────────────────
  {
    path: '/markets/impacts',
    name: 'markets-impacts',
    component: () => import('./pages/ImpactsPage.vue'),
    meta: { permission: 'markets:exposure', title: 'markets.impacts.title' },
  },
  {
    path: '/markets/impacts/:id',
    name: 'markets-impact',
    component: () => import('./pages/ImpactPage.vue'),
    meta: { permission: 'markets:exposure', title: 'markets.impacts.title' },
  },
  {
    path: '/markets/compare',
    name: 'markets-compare',
    component: () => import('./pages/ComparePage.vue'),
    meta: { permission: 'markets:exposure', title: 'markets.compare.title' },
  },
  {
    path: '/markets/suppliers',
    name: 'markets-suppliers',
    component: () => import('./pages/SuppliersPage.vue'),
    meta: { permission: 'markets:exposure', title: 'markets.suppliers.title' },
  },
  {
    path: '/markets/briefing',
    name: 'markets-briefing',
    component: () => import('./pages/BriefingPage.vue'),
    meta: { permission: 'markets:exposure', title: 'markets.briefing.title' },
  },

  // ── What comes in ──────────────────────────────────────────────────────────
  {
    path: '/markets/reports',
    name: 'markets-reports',
    component: () => import('./pages/ReportsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.reports.title' },
  },
  {
    path: '/markets/reports/:id',
    name: 'markets-report',
    component: () => import('./pages/ReportPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.reports.title' },
  },
  {
    path: '/markets/sources',
    name: 'markets-sources',
    component: () => import('./pages/SourcesPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.sources.title' },
  },
  {
    path: '/markets/topics',
    name: 'markets-topics',
    component: () => import('./pages/TopicsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.topics.title' },
  },

  // ── Deciding ───────────────────────────────────────────────────────────────
  {
    path: '/markets/decisions',
    name: 'markets-decisions',
    component: () => import('./pages/DecisionsPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.decisions.title' },
  },
  {
    path: '/markets/decisions/:id',
    name: 'markets-decision',
    component: () => import('./pages/DecisionPage.vue'),
    meta: { permission: 'markets:read', title: 'markets.decisions.title' },
  },
  {
    path: '/markets/alerts',
    name: 'markets-alerts',
    component: () => import('./pages/AlertsPage.vue'),
    meta: { permission: 'markets:decide', title: 'markets.alerts.title' },
  },
]

/**
 * One row that opens, and not fourteen.
 *
 * The work of this module is spread across dirección, whoever buys and whoever
 * analyses, but it is one subject in the sidebar. Giving each screen a
 * top-level entry would spend most of the few slots the next module installed
 * has the same claim to.
 *
 * The parent points at the PANEL and is guarded by `markets:read`, which is the
 * permission everybody who can open anything here holds: the shell draws a
 * parent row as a link to its own `to` and keeps that row visible to anybody
 * who may open one of its children, so pointing it at the impacts screen would
 * give somebody without `markets:exposure` a section header that 403s.
 *
 * There is no «Asistente IA» entry, and the design brief asks for one. The
 * platform already has an assistant: this module teaches it nine tools and a
 * playbook (backend/assistant.js), and the same panel answers about markets
 * alongside everything else. A second chat window would be a second history, a
 * second cost line, and a place where somebody asks about a quote and is told
 * this assistant only does markets.
 *
 * There is no «Configuración» entry either, for the same kind of reason: the
 * platform renders a module's settings screen from what the backend declares
 * (§4.2), so it exists already without a row here.
 */
export const navigation = [
  {
    to: '/markets',
    labelKey: 'markets.title',
    permission: 'markets:read',
    icon: 'radar',
    group: 'business',
    children: [
      { to: '/markets/radar', labelKey: 'markets.radar.title', permission: 'markets:read' },
      { to: '/markets/commodities', labelKey: 'markets.commodities.title', permission: 'markets:read' },
      { to: '/markets/impacts', labelKey: 'markets.impacts.title', permission: 'markets:exposure' },
      { to: '/markets/reports', labelKey: 'markets.reports.title', permission: 'markets:read' },
      { to: '/markets/compare', labelKey: 'markets.compare.title', permission: 'markets:exposure' },
      { to: '/markets/decisions', labelKey: 'markets.decisions.title', permission: 'markets:read' },
    ],
  },
]

/**
 * Strings this module contributes, merged per locale. Shell keys win on a
 * clash, so a module cannot rewrite platform copy.
 *
 * All four languages, and that is not politeness: a module whose translations
 * do not travel with it cannot be sold to the third client. That includes what
 * this module is called on the PLATFORM's own screens — Roles, Settings, Audit
 * and the assistant panel — because a module that makes somebody add keys to
 * the shell is not extractable.
 *
 * They live in four files rather than inline because there are seven hundred
 * keys of them, and because keeping the four side by side is what makes a
 * missing one visible while writing rather than at build time.
 */
const baseMessages = { es, ca, en, fr }

// El vocabulario de analítica del módulo, aparte porque es un contrato con el
// catálogo de `backend/analytics.js` y no copia de una pantalla. Ver ese fichero.
import { withAnalytics } from './i18n/analytics.js'

// What the assistant panel says while this module's tools run: another
// contract with the backend (backend/assistant.js), in its own file.
import { withToolLabels } from './i18n/toolLabels.js'

export const messages = withToolLabels(withAnalytics(baseMessages, 'markets'))

// Las tarjetas del asistente (markets.assistant.decision.*), en su propio
// fichero por la misma razón que la analítica: son un contrato con el backend.
import assistantMessages from './i18n/assistant.js'

for (const [locale, block] of Object.entries(assistantMessages)) {
  messages[locale].markets.assistant = { ...messages[locale].markets.assistant, ...block }
}
